import { Box, CircularProgress, Dialog, Paper } from "@material-ui/core";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { SketchPicker } from "react-color";
import DelayedRedirect from "../../../utils/DelayedRedirect";
import { Transition } from "../../../utils/ModalHelpers";
import { DisplayMessage } from "../../../services/helpers";
import {
  createTrait,
  updateTrait,
  getTraitById,
} from "../../../services/AssetTraits/assetTraitApisService";
import { AssetTraitsRoute } from "../../../constants/routePaths";
import AppBarComponent from "../../Common/Headers/AppBarComponent";
import TextFieldComponent from "../../Common/Inputs/TextFieldComponent";
import MultiOutletsDropdown from "../../Common/Dropdowns/MultiOutletsDropdown.js";
import ModifiedByInfo from "../../Common/Footers/ModifiedByInfo";

const colorPresets = [
  "#D32F2F",
  "#F57C00",
  "#FBC02D",
  "#388E3C",
  "#00897B",
  "#1976D2",
  "#3F51B5",
  "#8E24AA",
  "#6D4C41",
  "#616161",
];

const getReadableTextColor = (hex) => {
  const normalized = (hex || "").replace("#", "");
  if (normalized.length !== 6) return "rgba(0, 0, 0, 0.55)";
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "rgba(0, 0, 0, 0.6)" : "#fff";
};

const AssetTraitItem = ({ resources, traitId, accessibleMoboOutlets }) => {
  const isEditMode = useMemo(() => traitId && traitId > 0, [traitId]);
  const [redirect, setRedirect] = useState(null);
  const [refreshGrid, setRefreshGrid] = useState(false);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(true);
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [color, setColor] = useState("#FFFFFF");
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  // A new trait starts with every accessible outlet ticked; edit mode loads the stored selection.
  const [outletIds, setOutletIds] = useState(() =>
    isEditMode ? [] : (accessibleMoboOutlets ?? []).map((outlet) => outlet.id)
  );
  const [requestIsValid, setRequestIsValid] = useState(false);
  const [modifiedBy, setModifiedBy] = useState({ avatar: "", on: "", name: "" });

  const isRequestValid = useCallback(
    (displayError) => {
      const valid =
        name &&
        name.trim() !== "" &&
        value &&
        value.trim() !== "" &&
        outletIds.length > 0;

      if (!valid && displayError) {
        if (!name || name.trim() === "") {
          DisplayMessage(resources["InputOfNameIsMandatory"], "error");
        }
        if (!value || value.trim() === "") {
          DisplayMessage(resources["InputOfValueIsMandatory"], "error");
        }
        if (outletIds.length === 0) {
          DisplayMessage(resources["SelectionOfOutletIsMandatory"], "error");
        }
      }

      setRequestIsValid(valid);
      return valid;
    },
    [name, value, outletIds, resources]
  );

  const fetchTrait = useCallback(async () => {
    setRequestInProgress(true);
    const result = await getTraitById(traitId);
    if (result) {
      setName(result.name);
      setValue(result.value);
      setAbbreviation(result.abbreviation ?? "");
      setColor("#" + result.color);
      setOutletIds((result.outlets ?? []).map((o) => o.outletId));
      setModifiedBy({
        avatar: "",
        on: result.modifiedOn,
        name: result.modifiedBy,
      });
    } else {
      setRedirect(AssetTraitsRoute);
    }
    setRequestInProgress(false);
  }, [traitId]);

  useEffect(() => {
    if (isEditMode) {
      fetchTrait();
    }
  }, [isEditMode, fetchTrait]);

  useEffect(() => {
    isRequestValid(false);
  }, [isRequestValid]);

  const handleClose = () => {
    setDialogOpen(false);
    setRedirect(AssetTraitsRoute);
  };

  const handleSubmit = async () => {
    if (!isRequestValid(true)) return;

    setRequestInProgress(true);
    const trimmedAbbreviation =
      abbreviation && abbreviation.trim() !== "" ? abbreviation.trim() : null;
    const colorValue = color.replace("#", "");

    const body = isEditMode
      ? JSON.stringify({
          name,
          value,
          ...(trimmedAbbreviation ? { abbreviation: trimmedAbbreviation } : {}),
          color: colorValue,
          outletIds,
        })
      : JSON.stringify({
          name,
          value,
          abbreviation: trimmedAbbreviation,
          color: colorValue,
          outletIds,
        });

    const result = isEditMode
      ? await updateTrait(traitId, body)
      : await createTrait(body);

    if (!result) {
      setRequestInProgress(false);
      return;
    }

    setRefreshGrid(true);
    const successMessage = isEditMode
      ? resources["TraitWasUpdatedWithSuccess"]
      : resources["TraitWasCreatedWithSuccess"];
    DisplayMessage(successMessage, "success");
    setRedirect(AssetTraitsRoute);
  };

  const selectedColor = (color || "").toLowerCase();
  const isCustomColor = !colorPresets.some(
    (preset) => preset.toLowerCase() === selectedColor
  );

  const colorPickerStyles = {
    wrapper: { position: "relative", marginTop: 16, marginBottom: 8 },
    label: {
      display: "block",
      marginBottom: 8,
      fontSize: 12,
      color: "rgba(0, 0, 0, 0.6)",
    },
    swatchRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    swatch: {
      width: 30,
      height: 30,
      borderRadius: 4,
      cursor: "pointer",
      boxSizing: "border-box",
      boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.1)",
    },
    swatchSelected: {
      outline: "2px solid #1976d2",
      outlineOffset: 2,
    },
    customSwatch: {
      width: 30,
      height: 30,
      borderRadius: 4,
      cursor: "pointer",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#fff",
      border: "1px dashed rgba(0, 0, 0, 0.35)",
      color: "rgba(0, 0, 0, 0.55)",
      fontSize: 18,
      lineHeight: 1,
    },
    popover: {
      position: "absolute",
      zIndex: 2,
      top: "calc(100% + 8px)",
      left: 0,
    },
    cover: {
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  };

  return (
    <Box>
      {redirect && (
        <DelayedRedirect
          to={{ pathname: redirect, state: { refreshGrid: refreshGrid } }}
          delay={250}
        />
      )}
      <Dialog
        fullScreen
        open={dialogOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <AppBarComponent
          idName="trait"
          title={isEditMode ? resources["EditTrait"] : resources["NewTrait"]}
          resources={resources}
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          disabled={requestInProgress || !requestIsValid}
        />
        {requestInProgress ? (
          <Box className="centered-loader fadeIn">
            <CircularProgress />
          </Box>
        ) : (
          <Box className="container fadeIn">
            <Box className="py-4 grey">
              <Paper elevation={3} className="p-4 mt-3 mb-3">
                <Box className="d-flex form-group pt-3">
                  <Box className="col-3" />
                  <Box className="col-6">
                    <TextFieldComponent
                      value={name}
                      handleChange={(e) => setName(e.target.value)}
                      labelText={resources["Name"]}
                      id="name"
                      maxLength={50}
                      helperText={resources["InputOfNameIsMandatory"]}
                    />
                    <TextFieldComponent
                      value={value}
                      handleChange={(e) => setValue(e.target.value)}
                      labelText={resources["Value"]}
                      id="value"
                      maxLength={100}
                      helperText={resources["InputOfValueIsMandatory"]}
                    />
                    <TextFieldComponent
                      value={abbreviation}
                      handleChange={(e) => setAbbreviation(e.target.value)}
                      labelText={resources["Abbreviation"]}
                      id="abbreviation"
                      maxLength={2}
                    />
                    <MultiOutletsDropdown
                      resources={resources}
                      outlets={accessibleMoboOutlets}
                      outletIds={outletIds}
                      onOutletsChange={(ids) => setOutletIds(ids)}
                      showSelectAll
                      helperText={resources["SelectionOfOutletIsMandatory"]}
                      error={outletIds.length === 0}
                    />
                    <Box style={colorPickerStyles.wrapper}>
                      <span style={colorPickerStyles.label}>
                        {resources["Color"]}
                      </span>
                      <Box style={colorPickerStyles.swatchRow}>
                        {colorPresets.map((preset) => (
                          <div
                            key={preset}
                            style={{
                              ...colorPickerStyles.swatch,
                              background: preset,
                              ...(preset.toLowerCase() === selectedColor
                                ? colorPickerStyles.swatchSelected
                                : {}),
                            }}
                            onClick={() => {
                              setColor(preset);
                              setDisplayColorPicker(false);
                            }}
                          />
                        ))}
                        <div
                          id="color-picker"
                          style={{
                            ...colorPickerStyles.customSwatch,
                            ...(isCustomColor
                              ? {
                                  ...colorPickerStyles.swatchSelected,
                                  background: color,
                                  border: "none",
                                  color: getReadableTextColor(color),
                                  boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.1)",
                                }
                              : {}),
                          }}
                          onClick={() =>
                            setDisplayColorPicker(!displayColorPicker)
                          }
                        >
                          …
                        </div>
                      </Box>
                      {displayColorPicker ? (
                        <div style={colorPickerStyles.popover}>
                          <div
                            style={colorPickerStyles.cover}
                            onClick={() => setDisplayColorPicker(false)}
                          />
                          <SketchPicker
                            color={color}
                            disableAlpha
                            onChange={(c) => setColor(c.hex)}
                          />
                        </div>
                      ) : null}
                    </Box>
                  </Box>
                  <Box className="col-3" />
                </Box>
                {isEditMode && (
                  <ModifiedByInfo
                    resources={resources}
                    modifiedByAvatar={modifiedBy.avatar}
                    modifiedOn={modifiedBy.on}
                    modifiedByName={modifiedBy.name}
                    className="mt-3"
                  />
                )}
              </Paper>
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default AssetTraitItem;
